import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  ActivateFirewallRulesetRequest,
  CreateFirewallRulesetRequest,
  CreateFirewallRulesetVersionRequest,
  FirewallRulesetMutationResponse,
  FirewallRulesetSummary,
  FirewallRulesetVersionRecord,
  GetFirewallRulesetResponse,
  ReplayFirewallRulesetRequest,
  ReplayFirewallRulesetResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  activateFirewallRuleset,
  createFirewallRuleset,
  createFirewallRulesetVersion,
  deleteFirewallRuleset,
  getFirewallRuleset,
  getFirewallRulesetVersion,
  listFirewallRulesets,
  replayFirewallRuleset,
  type ListFirewallRulesetsFilter,
} from './firewallRulesets.ts';

export const FIREWALL_RULESETS_QUERY_KEY = ['firewall-rulesets'] as const;

export function useFirewallRulesets(
  filter: ListFirewallRulesetsFilter = {},
): UseQueryResult<FirewallRulesetSummary[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...FIREWALL_RULESETS_QUERY_KEY, filter],
    queryFn: ({ signal }) => listFirewallRulesets(filter, fetchImpl, signal),
  });
}

export function useFirewallRuleset(
  rulesetId: string,
): UseQueryResult<GetFirewallRulesetResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...FIREWALL_RULESETS_QUERY_KEY, 'detail', rulesetId],
    queryFn: ({ signal }) => getFirewallRuleset(rulesetId, fetchImpl, signal),
  });
}

/**
 * A version document is immutable once written, so it is cached without a
 * staleness window: refetching one can only ever return the same bytes.
 */
export function useFirewallRulesetVersion(
  rulesetId: string,
  version: number | null,
): UseQueryResult<FirewallRulesetVersionRecord> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...FIREWALL_RULESETS_QUERY_KEY, 'version', rulesetId, version],
    enabled: version !== null,
    staleTime: Infinity,
    queryFn: ({ signal }) => {
      if (version === null) {
        throw new Error('version query ran without a version');
      }
      return getFirewallRulesetVersion(rulesetId, version, fetchImpl, signal);
    },
  });
}

export function useCreateFirewallRuleset(): UseMutationResult<
  FirewallRulesetMutationResponse,
  Error,
  CreateFirewallRulesetRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createFirewallRuleset(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FIREWALL_RULESETS_QUERY_KEY });
    },
  });
}

export function useCreateFirewallRulesetVersion(): UseMutationResult<
  FirewallRulesetMutationResponse,
  Error,
  { ruleset_id: string } & CreateFirewallRulesetVersionRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleset_id, ...body }) =>
      createFirewallRulesetVersion(ruleset_id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FIREWALL_RULESETS_QUERY_KEY });
    },
  });
}

/**
 * A replay writes an evidence row and changes no ruleset, so nothing cached
 * here goes stale — the ruleset queries are deliberately NOT invalidated.
 */
export function useReplayFirewallRuleset(): UseMutationResult<
  ReplayFirewallRulesetResponse,
  Error,
  { ruleset_id: string } & ReplayFirewallRulesetRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: ({ ruleset_id, ...body }) => replayFirewallRuleset(ruleset_id, body, fetchImpl),
  });
}

export function useActivateFirewallRuleset(): UseMutationResult<
  FirewallRulesetMutationResponse,
  Error,
  { ruleset_id: string } & ActivateFirewallRulesetRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleset_id, ...body }) =>
      activateFirewallRuleset(ruleset_id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FIREWALL_RULESETS_QUERY_KEY });
    },
  });
}

export function useDeleteFirewallRuleset(): UseMutationResult<void, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rulesetId) => {
      await deleteFirewallRuleset(rulesetId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FIREWALL_RULESETS_QUERY_KEY });
    },
  });
}
